// frontend/src/services/notificationService.js
import axios from '../axiosConfig';
import { hasRoleLevel } from '../utils/roleUtils';

/**
 * Frontend service for notification system management
 * Handles API calls for notification settings, preferences, and configuration
 */
class NotificationService {
    constructor() {
        this.cache = new Map();
        this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
    }

    // ==================== MENU VISIBILITY ====================

    /**
     * Check if notification menu should be visible for an entity
     * Implements three-tier visibility rules
     * @param {number} entityId - Entity ID
     * @returns {Promise<Object>} Visibility data
     */
    async checkMenuVisibility(entityId) {
        const cacheKey = `menu_visibility_${entityId}`;
        
        if (this.cache.has(cacheKey)) {
            const cached = this.cache.get(cacheKey);
            if (Date.now() - cached.timestamp < this.cacheTimeout) {
                return cached.data;
            }
        }
        
        try {
            const response = await axios.get(`/api/notification-settings/menu-visibility/${entityId}`);
            const data = response.data;
            
            // Cache the result
            this.cache.set(cacheKey, {
                data,
                timestamp: Date.now()
            });
            
            return data;
        } catch (error) {
            console.error('Error checking notification visibility:', error);
            return { 
                success: false,
                visible: false, 
                channels: {},
                reason: 'error'
            };
        }
    }

    // ==================== EVENT TYPES ====================

    /**
     * Get all notification event types
     * @param {number} entityId - Entity ID
     * @returns {Promise<Array>} Event types
     */
    async getEventTypes(entityId) {
        const cacheKey = `event_types_${entityId}`;
        
        if (this.cache.has(cacheKey)) {
            const cached = this.cache.get(cacheKey);
            if (Date.now() - cached.timestamp < this.cacheTimeout) {
                return cached.data;
            }
        }
        
        try {
            console.log(`NotificationService: Calling /api/notification-settings/${entityId}/event-types`);
            const response = await axios.get(`/api/notification-settings/${entityId}/event-types`);
            const data = response.data.data;
            console.log('NotificationService: Event types response:', { entityId, data });
            
            // Cache the result
            this.cache.set(cacheKey, {
                data,
                timestamp: Date.now()
            });
            
            return data;
        } catch (error) {
            console.error('Error getting event types:', error.response?.data || error.message);
            return [];
        }
    }

    // ==================== USER PREFERENCES ====================

    /**
     * Get user notification preferences for an entity
     * @param {number} entityId - Entity ID
     * @returns {Promise<Array>} User preferences
     */
    async getUserPreferences(entityId) {
        try {
            const response = await axios.get(`/api/notification-settings/${entityId}/preferences`);
            return response.data.data || [];
        } catch (error) {
            console.error('Error getting user preferences:', error);
            return [];
        }
    }

    /**
     * Save user notification preferences
     * @param {number} entityId - Entity ID
     * @param {Array} preferences - Preferences array
     * @returns {Promise<Object>} API response
     */
    async saveUserPreferences(entityId, preferences) {
        try {
            const response = await axios.put(`/api/notification-settings/${entityId}/preferences`, preferences);
            
            // Clear related cache
            this.clearCacheByPattern(`user_preferences_${entityId}`);
            
            return response.data;
        } catch (error) {
            console.error('Error saving user preferences:', error);
            throw error;
        }
    }

    /**
     * Reset user preferences to defaults
     * @param {number} entityId - Entity ID
     * @returns {Promise<Object>} API response
     */
    async resetUserPreferences(entityId) {
        try {
            const response = await axios.delete(`/api/notification-settings/${entityId}/preferences`);
            
            // Clear related cache
            this.clearCacheByPattern(`user_preferences_${entityId}`);
            
            return response.data;
        } catch (error) {
            console.error('Error resetting user preferences:', error);
            throw error;
        }
    }

    /**
     * Get user notification statistics
     * @param {number} entityId - Entity ID
     * @param {number} days - Number of days (default 30)
     * @returns {Promise<Object>} Statistics
     */
    async getUserStatistics(entityId, days = 30) {
        try {
            const response = await axios.get(`/api/notification-settings/${entityId}/user-statistics?days=${days}`);
            return response.data.data || {};
        } catch (error) {
            console.error('Error getting user statistics:', error);
            return {};
        }
    }

    // ==================== ADMIN: CHANNELS ====================

    /**
     * Check if user has permission for a specific channel
     * @param {number} entityId - Entity ID
     * @param {string} channel - Channel name
     * @returns {Promise<boolean>} Has permission
     */
    async checkChannelPermission(entityId, channel) {
        try {
            if (!channel) {
                return false;
            }

            const userRole = localStorage.getItem('role') || 'user';
            // Prefer backend-defined roles surfaced via menu visibility, but keep
            // sane defaults in case that payload is unavailable (offline/legacy).
            const FALLBACK_REQUIRED_ROLE = 'super_admin';
            const FALLBACK_CHANNEL_ROLE = {
                sms: 'admin',
                email: 'super_admin',
                push: 'super_admin'
            };

            let required = FALLBACK_CHANNEL_ROLE[channel] || FALLBACK_REQUIRED_ROLE;

            if (entityId) {
                // Menu visibility already caches the response per entity, so this
                // reuses the same payload that the notifications UI depends on.
                const visibility = await this.checkMenuVisibility(entityId);
                const channelMeta = visibility?.globalChannelsWithRoles?.[channel];
                if (channelMeta?.required_role) {
                    required = channelMeta.required_role;
                }
            }

            // Reuse shared hierarchy helper so role ordering stays consistent with the rest of the app.
            return hasRoleLevel(userRole, required);
        } catch (e) {
            console.warn('checkChannelPermission fallback to false', e);
            return false;
        }
    }

    /**
     * Get entity channel configuration (admin only)
     * @param {number} entityId - Entity ID
     * @returns {Promise<Array>} Channel configurations
     */
    async getEntityChannels(entityId) {
        try {
            console.log(`NotificationService: Calling /api/notification-settings/${entityId}/channels`);
            const response = await axios.get(`/api/notification-settings/${entityId}/channels`);
            const data = response.data.data || [];
            console.log('NotificationService: Channels response:', { entityId, data });
            return data;
        } catch (error) {
            console.error('Error getting entity channels:', error.response?.data || error.message);
            return [];
        }
    }

    /**
     * Update channel configuration (admin only)
     * @param {number} entityId - Entity ID
     * @param {string} channel - Channel type
     * @param {Object} config - Configuration object
     * @returns {Promise<Object>} API response
     */
    async updateChannelConfig(entityId, channel, config) {
        try {
            const response = await axios.put(`/api/notification-settings/${entityId}/channels/${channel}`, config);
            
            // Clear related cache
            this.clearCacheByPattern(`menu_visibility_${entityId}`);
            this.clearCacheByPattern(`entity_channels_${entityId}`);
            
            return response.data;
        } catch (error) {
            console.error('Error updating channel config:', error);
            throw error;
        }
    }

    /**
     * Test channel configuration (admin only)
     * @param {number} entityId - Entity ID
     * @param {string} channel - Channel type
     * @returns {Promise<Object>} Test result
     */
    async testChannelConfig(entityId, channel) {
        try {
            const response = await axios.post(`/api/notification-settings/${entityId}/channels/${channel}/test`);
            return response.data;
        } catch (error) {
            console.error('Error testing channel config:', error);
            throw error;
        }
    }

    /**
     * Get channel statistics (admin only)
     * @param {number} entityId - Entity ID
     * @param {number} days - Number of days (default 30)
     * @returns {Promise<Object>} Statistics
     */
    async getChannelStatistics(entityId, days = 30) {
        try {
            const response = await axios.get(`/api/notification-settings/${entityId}/statistics?days=${days}`);
            return response.data.data || {};
        } catch (error) {
            console.error('Error getting channel statistics:', error);
            return {};
        }
    }

    // ==================== ADMIN: TEMPLATES ====================

    /**
     * Get all templates for entity (admin only)
     * @param {number} entityId - Entity ID
     * @returns {Promise<Array>} Templates
     */
    async getEntityTemplates(entityId) {
        try {
            const response = await axios.get(`/api/notification-settings/${entityId}/templates`);
            return response.data.data || [];
        } catch (error) {
            console.error('Error getting entity templates:', error);
            return [];
        }
    }

    /**
     * Get specific template (admin only)
     * @param {number} entityId - Entity ID
     * @param {number} eventTypeId - Event type ID
     * @param {string} channel - Channel
     * @param {string} language - Language code (optional)
     * @returns {Promise<Object|null>} Template
     */
    async getTemplate(entityId, eventTypeId, channel, languageOrOptions = null) {
        try {
            const searchParams = new URLSearchParams();

            if (typeof languageOrOptions === 'string') {
                searchParams.append('language', languageOrOptions);
            } else if (languageOrOptions && typeof languageOrOptions === 'object') {
                if (languageOrOptions.language) {
                    searchParams.append('language', languageOrOptions.language);
                }
                if (languageOrOptions.includeGlobalFallback === false) {
                    searchParams.append('includeGlobalFallback', 'false');
                }
            }

            const queryString = searchParams.toString();
            const url = `/api/notification-settings/${entityId}/templates/${eventTypeId}/${channel}${queryString ? `?${queryString}` : ''}`;

            const response = await axios.get(url);
            return response.data.data;
        } catch (error) {
            console.error('Error getting template:', error.response?.data || error.message);
            return null;
        }
    }

    /**
     * Save template (admin only)
     * @param {number} entityId - Entity ID
     * @param {number} eventTypeId - Event type ID
     * @param {string} channel - Channel
     * @param {Object} templateData - Template data
     * @returns {Promise<Object>} API response
     */
    async saveTemplate(entityId, eventTypeId, channel, templateData) {
        try {
            const response = await axios.put(`/api/notification-settings/${entityId}/templates/${eventTypeId}/${channel}`, templateData);
            
            // Clear related cache
            this.clearCacheByPattern(`template_${entityId}_${eventTypeId}_${channel}`);
            
            return response.data;
        } catch (error) {
            console.error('Error saving template:', error);
            throw error;
        }
    }

    /**
     * Reset template to global (delete entity template)
     * @param {number} entityId - Entity ID
     * @param {number} eventTypeId - Event type ID
     * @param {string} channel - Channel
     * @returns {Promise<Object>} Global template that will now be used
     */
    async resetTemplateToGlobal(entityId, eventTypeId, channel) {
        try {
            const response = await axios.delete(`/api/notification-settings/${entityId}/templates/${eventTypeId}/${channel}`);

            // Clear related cache
            this.clearCacheByPattern(`template_${entityId}_${eventTypeId}_${channel}`);

            return response.data;
        } catch (error) {
            console.error('Error resetting template to global:', error);
            throw error;
        }
    }

    /**
     * Preview global template (before customization)
     * @param {number} entityId - Entity ID
     * @param {number} eventTypeId - Event type ID
     * @param {string} channel - Channel
     * @param {string} language - Language code (optional)
     * @returns {Promise<Object>} Global template
     */
    async previewGlobalTemplate(entityId, eventTypeId, channel, language = null) {
        try {
            const params = language ? `?language=${language}` : '';
            const response = await axios.get(`/api/notification-settings/${entityId}/templates/${eventTypeId}/${channel}/global-preview${params}`);
            return response.data.data;
        } catch (error) {
            console.error('Error previewing global template:', error);
            throw error;
        }
    }

    /**
     * Copy global template to entity (for customization)
     * @param {number} entityId - Entity ID
     * @param {number} eventTypeId - Event type ID
     * @param {string} channel - Channel
     * @returns {Promise<Object>} New entity template
     */
    async copyGlobalTemplateToEntity(entityId, eventTypeId, channel) {
        try {
            const response = await axios.post(`/api/notification-settings/${entityId}/templates/${eventTypeId}/${channel}/copy-global`);

            // Clear related cache
            this.clearCacheByPattern(`template_${entityId}_${eventTypeId}_${channel}`);

            return response.data;
        } catch (error) {
            console.error('Error copying global template to entity:', error);
            throw error;
        }
    }

    // ==================== TESTING ====================

    /**
     * Send test notification (admin only)
     * @param {number} entityId - Entity ID
     * @param {string} eventType - Event type key
     * @param {string} channel - Channel
     * @param {Object} testData - Test data (optional)
     * @returns {Promise<Object>} Test result
     */
    async sendTestNotification(entityId, eventType, channel, testData = {}) {
        try {
            const response = await axios.post(`/api/notifications/${entityId}/test`, {
                type: eventType,
                channel,
                ...testData
            });
            return response.data;
        } catch (error) {
            console.error('Error sending test notification:', error);
            throw error;
        }
    }

    // ==================== VALIDATION ====================

    /**
     * Validate notification preferences before saving
     * @param {Array} preferences - Preferences to validate
     * @param {Array} eventTypes - Available event types
     * @param {Array} availableChannels - Available channels
     * @returns {Object} Validation result
     */
    validatePreferences(preferences, eventTypes, availableChannels) {
        const errors = [];
        const warnings = [];

        for (const pref of preferences) {
            // Check if event type exists
            const eventType = eventTypes.find(et => et.id === pref.eventTypeId);
            if (!eventType) {
                errors.push(`Invalid event type ID: ${pref.eventTypeId}`);
                continue;
            }

            // Check advance time validation
            if (eventType.supports_advance_time && pref.advanceMinutes !== null) {
                const advanceMinutes = parseInt(pref.advanceMinutes);
                if (isNaN(advanceMinutes)) {
                    errors.push(`Invalid advance minutes for ${eventType.display_name_key}: ${pref.advanceMinutes}`);
                } else if (advanceMinutes < eventType.min_advance_minutes || advanceMinutes > eventType.max_advance_minutes) {
                    errors.push(`Advance minutes for ${eventType.display_name_key} must be between ${eventType.min_advance_minutes} and ${eventType.max_advance_minutes}`);
                }
            }

            // Check if user has any channels enabled
            const hasEnabledChannel = availableChannels.some(channel => pref[`${channel}Enabled`]);
            if (!hasEnabledChannel) {
                warnings.push(`No channels enabled for ${eventType.display_name_key}`);
            }

            // Validate quiet hours format
            if (pref.quietHoursStart && !this.isValidTimeFormat(pref.quietHoursStart)) {
                errors.push(`Invalid quiet hours start time format: ${pref.quietHoursStart}`);
            }
            if (pref.quietHoursEnd && !this.isValidTimeFormat(pref.quietHoursEnd)) {
                errors.push(`Invalid quiet hours end time format: ${pref.quietHoursEnd}`);
            }
        }

        return {
            isValid: errors.length === 0,
            errors,
            warnings
        };
    }

    /**
     * Validate time format (HH:MM)
     * @param {string} timeStr - Time string
     * @returns {boolean} Is valid
     */
    isValidTimeFormat(timeStr) {
        if (!timeStr) return false;
        const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
        return timeRegex.test(timeStr);
    }

    // ==================== CACHE MANAGEMENT ====================

    /**
     * Clear cache by pattern
     * @param {string} pattern - Pattern to match
     */
    clearCacheByPattern(pattern) {
        for (const key of this.cache.keys()) {
            if (key.includes(pattern)) {
                this.cache.delete(key);
            }
        }
    }

    /**
     * Clear all cache
     */
    clearCache() {
        this.cache.clear();
    }

    /**
     * Get cache statistics
     * @returns {Object} Cache stats
     */
    getCacheStats() {
        return {
            size: this.cache.size,
            keys: Array.from(this.cache.keys())
        };
    }

    // ==================== UTILITY METHODS ====================

    /**
     * Get channel icon name for UI
     * @param {string} channel - Channel name
     * @returns {string} Icon name
     */
    getChannelIcon(channel) {
        const icons = {
            email: 'Mail',
            sms: 'MessageSquare',
            push: 'Smartphone'
        };
        return icons[channel] || 'Bell';
    }

    /**
     * Get event category icon
     * @param {string} category - Event category
     * @returns {string} Icon name
     */
    getCategoryIcon(category) {
        const icons = {
            booking: 'Calendar',
            trip: 'Navigation',
            account: 'User',
            system: 'Settings'
        };
        return icons[category] || 'Bell';
    }

    /**
     * Format notification preference for display
     * @param {Object} preference - Preference object
     * @param {Array} availableChannels - Available channels
     * @returns {Object} Formatted preference
     */
    formatPreference(preference, availableChannels) {
        const enabledChannels = availableChannels.filter(channel => 
            preference[`${channel}Enabled`]
        );

        return {
            ...preference,
            enabledChannels,
            hasAdvanceTime: preference.advanceMinutes !== null && preference.supportsAdvanceTime,
            hasQuietHours: preference.quietHoursStart && preference.quietHoursEnd,
            summary: `${enabledChannels.length}/${availableChannels.length} channels enabled`
        };
    }
}

export default new NotificationService();
